import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/shared/auth/useAuth";
import { Button } from "@/shared/components/ui/button";
import type { Role } from "@/shared/types/role";

// Firebase + the /auth/me round-trip normally resolves in <2s. If we're still
// "loading" after this long, something is wrong (slow network, blocked request)
// — surface a retry instead of silently bouncing to /login.
const LOADING_STUCK_THRESHOLD_MS = 12_000;

interface RequireRoleProps {
  roles: readonly Role[];
  children: ReactNode;
}

export function RequireRole({ roles, children }: RequireRoleProps) {
  const { status, user } = useAuth();
  const location = useLocation();
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    if (status !== "loading") {
      setStuck(false);
      return;
    }
    const timer = setTimeout(() => setStuck(true), LOADING_STUCK_THRESHOLD_MS);
    return () => clearTimeout(timer);
  }, [status]);

  if (status === "loading") {
    if (stuck) {
      return (
        <div
          data-testid="auth-stuck"
          className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center"
        >
          <p className="max-w-md text-ge-secondary">
            Still connecting. This is taking longer than usual.
          </p>
          <Button data-testid="auth-stuck-retry" variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      );
    }
    return (
      <div
        data-testid="auth-loading"
        className="flex min-h-screen items-center justify-center text-ge-secondary"
      >
        Loading…
      </div>
    );
  }

  if (status === "unauthenticated" || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Superadmin passes every gate; otherwise the role must be in the allowed set.
  if (!user.superadmin && !roles.includes(user.role)) {
    return <Navigate to="/403" replace />;
  }

  return <>{children}</>;
}
